import type { CampaignCreationTask } from "@adpilot/shared";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { usePermission } from "@/hooks/use-permission";
import { campaignCreationTaskStageKey } from "@/lib/campaign-creation-tasks";

interface SmartCreationResultViewProps {
  task: CampaignCreationTask;
  canMutate: boolean;
  pending: boolean;
  stale: boolean;
  onRetry(): void;
  onReconcile(nodeKey: string): void;
  onRefresh(): void;
}

export function SmartCreationResultView({
  task,
  canMutate,
  pending,
  stale,
  onRetry,
  onReconcile,
  onRefresh,
}: SmartCreationResultViewProps) {
  const { t } = useTranslation("smartCreation");
  const { t: layoutT } = useTranslation("layout");
  const { hasModule } = usePermission();
  const simulated = task.executionMode !== "amazon" || task.status === "simulated";
  const complete = task.status === "completed" || task.status === "simulated";
  const active = task.status === "queued" || task.status === "running";
  const liveCompleted =
    task.status === "completed" && !simulated && task.identityVerification === "verified";
  const canRecover = canMutate && !stale && !complete && task.status !== "canceled";
  const unresolved = task.items.filter((item) => item.status === "unknown");
  const stage = layoutT(`creationProgress.${campaignCreationTaskStageKey(task)}`);

  return (
    <div
      className="paper-scrollbar flex min-h-0 flex-1 flex-col items-start gap-3 overflow-y-auto p-6"
      data-slot="campaign-creation-task-result"
    >
      <p role="status" aria-live="polite" className="text-base font-medium">
        {stage}
      </p>
      {simulated && <p className="text-sm text-muted-foreground">{t("task.simulationNotice")}</p>}
      {active && <p className="text-sm text-muted-foreground">{t("task.backgroundNotice")}</p>}
      {liveCompleted && (
        <p className="text-sm text-muted-foreground">
          {t("task.completedCount", { count: task.counts.campaigns.active })}
        </p>
      )}
      {!active && !complete && task.lastError && (
        <p className="max-w-2xl text-sm text-muted-foreground">{task.lastError.message}</p>
      )}
      {stale && <p className="text-sm text-muted-foreground">{t("task.stale")}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {liveCompleted && task.managedGroupId && hasModule("ai") && (
          <Button
            variant="outline"
            render={<Link to="/managed-groups/$id" params={{ id: task.managedGroupId }} />}
          >
            {t("task.openGroup")}
          </Button>
        )}
        {canRecover && task.allowedActions.includes("retry") && (
          <Button variant="outline" disabled={!canMutate} loading={pending} onClick={onRetry}>
            {t("task.retry")}
          </Button>
        )}
        {canRecover &&
          task.allowedActions.includes("reconcile") &&
          unresolved.map((item) => (
            <Button
              key={item.nodeKey}
              variant="outline"
              disabled={!canMutate}
              loading={pending}
              onClick={() => onReconcile(item.nodeKey)}
            >
              {t("task.reconcile", { name: item.campaignName })}
            </Button>
          ))}
        {stale && (
          <Button variant="outline" disabled={pending} onClick={onRefresh}>
            {t("task.refresh")}
          </Button>
        )}
      </div>
    </div>
  );
}
